import { useCallback, useRef, useState, type FormEvent } from "react";

interface UseFormSubmissionOptions {
  onSubmit: (event: FormEvent<HTMLFormElement>) => void | Promise<void>;
  disabled?: boolean;
}

export default function useFormSubmission({
  onSubmit,
  disabled = false,
}: UseFormSubmissionOptions) {
  const submitting = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      event.target !== event.currentTarget ||
      !event.currentTarget.isConnected ||
      event.currentTarget.closest('[hidden], [inert], [aria-hidden="true"]') ||
      disabled ||
      submitting.current
    ) {
      return;
    }

    submitting.current = true; // Block another submit before React renders the pending state.
    setIsSubmitting(true);
    try {
      await onSubmit(event);
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
    }
  }, [disabled, onSubmit]);

  return { handleSubmit, isSubmitting };
}
